import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, Loader2, ArrowRight, CheckCircle } from 'lucide-react';
import api from '../services/api';

const ForgotPasswordPage = () => {
  const { language } = useLanguage();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const content = {
    en: {
      title: "Forgot Password?",
      subtitle: "Enter your email and we'll send you a reset link",
      email: "Email Address",
      submit: "Send Reset Link",
      backToLogin: "Back to Login",
      backToHome: "Back to Home",
      successTitle: "Check Your Email",
      successMessage: "If an account with that email exists, we've sent a password reset link. Please check your inbox and spam folder.",
      error: "Something went wrong. Please try again."
    },
    ar: {
      title: "نسيت كلمة المرور؟",
      subtitle: "أدخل بريدك الإلكتروني وسنرسل لك رابط إعادة التعيين",
      email: "البريد الإلكتروني",
      submit: "إرسال رابط إعادة التعيين",
      backToLogin: "العودة لتسجيل الدخول",
      backToHome: "العودة للرئيسية",
      successTitle: "تحقق من بريدك الإلكتروني",
      successMessage: "إذا كان هناك حساب بهذا البريد الإلكتروني، فقد أرسلنا رابط إعادة تعيين كلمة المرور. يرجى التحقق من صندوق الوارد والبريد العشوائي.",
      error: "حدث خطأ ما. يرجى المحاولة مرة أخرى."
    }
  };

  const t = content[language];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await api.post('/api/auth/forgot-password', { email });
      setSuccess(true);
    } catch (err) {
      // Always show success to prevent email enumeration
      setSuccess(true);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-emerald-50 flex items-center justify-center p-4" dir={language === 'ar' ? 'rtl' : 'ltr'}>
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">{t.successTitle}</h1>
            <p className="text-gray-600 mb-6">{t.successMessage}</p>
            <Link to="/login">
              <Button className="w-full bg-teal-600 hover:bg-teal-700 text-white">
                {t.backToLogin}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-emerald-50 flex items-center justify-center p-4" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center text-teal-600 hover:text-teal-700 mb-6">
            <ArrowRight className={`w-4 h-4 ${language === 'ar' ? 'ml-2 rotate-180' : 'mr-2 rotate-180'}`} />
            {t.backToHome}
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t.title}</h1>
          <p className="text-gray-600">{t.subtitle}</p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-700 font-medium">
                {t.email}
              </Label>
              <div className="relative">
                <Mail className={`absolute top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 ${language === 'ar' ? 'right-3' : 'left-3'}`} />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className={`${language === 'ar' ? 'pr-10 text-right' : 'pl-10'} h-12`}
                  required
                  data-testid="forgot-email-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading || !email}
              className="w-full h-12 bg-teal-600 hover:bg-teal-700 text-white font-semibold text-lg"
              data-testid="forgot-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                t.submit
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/login" className="text-teal-600 hover:text-teal-700 font-medium">
              {t.backToLogin}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
